import { format, isValid, max, min, parse, parseISO } from 'date-fns';

export type FilterChip = {
    colId: string;
    label: string;
    value: string;
};

export type ColDefLike = { colId?: string; field?: string; headerName?: string };

type FilterEntry = {
    filter?: unknown;
    value?: unknown;
    dateFrom?: unknown;
    dateTo?: unknown;
    type?: unknown;
};

function parseFilterDate(value: unknown): Date | null {
    if (value instanceof Date) {
        return isValid(value) ? value : null;
    }

    const raw = String(value);
    const withTime = parse(raw, 'yyyy-MM-dd HH:mm:ss', new Date());
    if (isValid(withTime)) {
        return withTime;
    }

    const iso = parseISO(raw);
    return isValid(iso) ? iso : null;
}

function formatDateValue(value: unknown): string {
    const date = parseFilterDate(value);
    return date ? format(date, 'MMM d, yyyy') : String(value);
}

function formatDateRange(fromValue: unknown, toValue: unknown): string {
    const from = parseFilterDate(fromValue);
    const to = parseFilterDate(toValue);
    if (from && to) {
        const start = min([from, to]);
        const end = max([from, to]);
        return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
    }

    return `${formatDateValue(fromValue)} – ${formatDateValue(toValue)}`;
}

function formatFilterValue(colId: string, entry: FilterEntry, valueMaps?: Record<string, Record<string, string>>): string | null {
    if (entry.value) {
        const mapped = valueMaps?.[colId]?.[String(entry.value)] ?? valueMaps?.[colId]?.[String(entry.value).toLowerCase()];
        if (mapped) {
            return mapped;
        }
    }

    if (entry.dateFrom) {
        if (entry.type === 'inRange' && entry.dateTo) {
            return formatDateRange(entry.dateFrom, entry.dateTo);
        }
        const from = formatDateValue(entry.dateFrom);
        if (entry.type === 'lessThan') {
            return `before ${from}`;
        }
        if (entry.type === 'greaterThan') {
            return `after ${from}`;
        }
        return from;
    }

    if (entry.filter != null && entry.filter !== '') {
        return String(entry.filter);
    }

    return entry.value ? String(entry.value) : null;
}

export function labelsFromColDefs(colDefs: ColDefLike[]): Record<string, string> {
    return Object.fromEntries(
        colDefs.flatMap((col) => {
            const id = col.colId ?? col.field;
            return id && col.headerName ? [[id, col.headerName]] : [];
        }),
    );
}

export function toFilterChips(
    filterModel: Record<string, unknown> | null | undefined,
    columnLabels: Record<string, string>,
    valueMaps?: Record<string, Record<string, string>>,
): FilterChip[] {
    if (!filterModel) {
        return [];
    }

    return Object.entries(filterModel).flatMap(([colId, raw]) => {
        if (!raw || typeof raw !== 'object') {
            return [];
        }
        const value = formatFilterValue(colId, raw as FilterEntry, valueMaps);
        return value ? [{ colId, label: columnLabels[colId] ?? colId, value }] : [];
    });
}

/** Hide chips whose displayed value still matches the page default (e.g. QA status = Submitted). */
export function toNonDefaultFilterChips(
    filterModel: Record<string, unknown> | null | undefined,
    defaultFilterModel: Record<string, unknown> | null | undefined,
    columnLabels: Record<string, string>,
    valueMaps?: Record<string, Record<string, string>>,
): FilterChip[] {
    const chips = toFilterChips(filterModel, columnLabels, valueMaps);
    const defaultChips = toFilterChips(defaultFilterModel, columnLabels, valueMaps);
    if (defaultChips.length === 0) {
        return chips;
    }

    const defaultValueByColId = new Map(defaultChips.map((chip) => [chip.colId, chip.value]));
    return chips.filter((chip) => defaultValueByColId.get(chip.colId) !== chip.value);
}
